import { useAccountBalanceStrings } from "module/vault/hook/useAccountBalanceStrings"

import { Box, Center, Text, Tooltip } from "@chakra-ui/react"
import { MotionBox } from "component/Motion"
import { useMotionValue, useTransform } from "framer-motion"
import { useEffect } from "react"

const NEEDLE_WIDTH = 4

interface RiskBarProps {
    heightX?: number
    isDisabled?: boolean
    isRaw?: boolean
}

function RiskBar({ heightX = 1, isDisabled, isRaw }: RiskBarProps) {
    const { marginRatioVal } = useAccountBalanceStrings()
    const value = marginRatioVal * 100
    const propValue = useMotionValue(value)
    const visualValue = useTransform(propValue, [0, 6.25, 50, 100], [0, 33.33, 66.66, 100], { clamp: true })
    const rawValue = useTransform(propValue, [0, 100], [0, 100], { clamp: true })

    // fire once here to avoid update delay, don't know why
    propValue.set(value)

    useEffect(() => {
        // update propValue with value
        if (propValue.get() !== value) {
            setTimeout(() => {
                propValue.set(value)
            }, 100)
        }
    }, [value, propValue])

    return (
        <MotionBox position="relative" w="100%" h="auto">
            {/* risk bar needle*/}
            <MotionBox
                display={isDisabled ? "none" : "block"}
                textAlign="center"
                zIndex={1}
                opacity={value === 0 ? 0 : 1}
                initial={{ left: "0%" }}
                animate={{
                    background: "white",
                    left: `calc(${isRaw ? rawValue.get() : visualValue.get()}% - ${NEEDLE_WIDTH}px)`,
                }}
                h={`calc( ${heightX} * 32px + 4px)`}
                bottom={"10px"}
                w={NEEDLE_WIDTH + "px"}
                position="absolute"
                borderRadius="full"
                boxShadow="0px 0px 4px rgba(0, 0, 0, 0.25)"
            >
                {/* Hidden needle feature */}
                <Center position="relative" textAlign="center" top={-6}>
                    <MotionBox
                        whileHover={{ opacity: 1, scale: 1, y: 0 }}
                        initial={{ opacity: 0, scale: 0.5, y: 5 }}
                        textAlign="center"
                        pt={0}
                        h={`calc( ${heightX} * 54px)`}
                    >
                        {value <= 6.25 ? "💀" : value < 50 ? "😨" : value < 100 ? "😄" : "😄"}
                    </MotionBox>
                </Center>
            </MotionBox>
            <MotionBox
                my={2}
                h={`calc( ${heightX} * 32px)`}
                w="full"
                borderRadius="8px"
                overflow="hidden"
                position="relative"
                bg={isDisabled ? "blackAlpha.600" : "none"}
                bgGradient={
                    isDisabled
                        ? "none"
                        : isRaw
                        ? "linear-gradient(90deg, rgba(241,103,105,1) 0%, rgba(241,103,105,1) 6.25%, rgba(255,180,89,1) 6.25%, rgba(255,180,89,1) 50%, rgba(60,234,170,1) 50%, rgba(60,234,170,1) 100%)"
                        : "linear-gradient(90deg, rgba(241,103,105,1) 0%, rgba(241,103,105,1) 33.33%, rgba(255,180,89,1) 33.33%, rgba(255,180,89,1) 66.66%, rgba(60,234,170,1) 66.66%, rgba(60,234,170,1) 100%)"
                }
                bgPosition="center"
                bgRepeat="no-repeat"
                bgSize="cover"
            />
            <Box w="full" h={1} position="relative" opacity={isDisabled ? 0.5 : 1}>
                <Tooltip hasArrow label="Your position will be liquidated if margin ratio is below 6.25%">
                    <Text
                        position="absolute"
                        opacity={0.65}
                        fontSize={"xs"}
                        left={isRaw ? "calc(6.25% - 1em)" : "calc(33.33% - 1em)"}
                    >
                        6.25%
                    </Text>
                </Tooltip>
                <Tooltip hasArrow label="Keep above 50% for lower risk">
                    <Text
                        position="absolute"
                        left={isRaw ? "calc(50% - 0.6em)" : "calc(66.66% - 1em)"}
                        opacity={0.65}
                        fontSize={"xs"}
                    >
                        50%
                    </Text>
                </Tooltip>
            </Box>
        </MotionBox>
    )
}

export { RiskBar }
